import { ArrowForward } from '@mui/icons-material';
import { Stack } from '@mui/material';
import { Tool } from '@procureai/agents';
import { IconLink } from '@procureai/ui-mui';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ChatLoadingIndicator } from '../ChatMessage/indicators/ChatLoadingIndicator';
import { ChatToolErrorIndicator } from '../ChatMessage/indicators/ChatToolErrorIndicator';
import { ChatToolResultIndicator } from '../ChatMessage/indicators/ChatToolResultIndicator';
import {
  SvbrgHandoverToRouteInput,
  SvbrgHandoverToRouteIntent,
  SvbrgHandoverToRouteOutput,
} from '@/generated/gql/graphql';
import { I18nNamespace } from '@/legacy/common/types';
import { RouteId } from '~/routes';

import { ToolResultComponent } from '.';

const INTENT_TO_ROUTE: Record<SvbrgHandoverToRouteIntent, { title: string }> = {
  REQUEST_FROM_FORM: {
    title: 'tools.handoverToRoute.optionFormRoute',
  },
  REQUEST_FROM_QUOTE: {
    title: 'tools.handoverToRoute.optionQuoteRoute',
  },
};

export const HandoverToRouteTool: ToolResultComponent<
  Tool.handoverToRoute,
  SvbrgHandoverToRouteInput,
  SvbrgHandoverToRouteOutput
> = ({ tool, toolName }) => {
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: `Chat.tools.${toolName}` });

  const navigate = useNavigate();

  const handleContinue = useCallback(
    async (chatSessionId: string) => {
      navigate(RouteId.intakeDetail(chatSessionId));
    },
    [navigate],
  );

  if (!tool.input?.intent || !(tool.input.intent in INTENT_TO_ROUTE)) {
    return <></>;
  }

  const title = INTENT_TO_ROUTE[tool.input.intent].title;
  switch (tool.state) {
    case 'input-streaming':
    case 'input-available':
      return <ChatLoadingIndicator message={t(`call`, { to: t(title) })} />;

    case 'output-available':
      if (!tool.output.chatSessionId) {
        // if e.g. the user parsing fails, the chatSessionId will not be set
        return <ChatToolErrorIndicator message={t(`error`)} />;
      } else {
        return (
          <ChatToolResultIndicator message={t(`call`, { to: t(title) })} defaultOpen={true} disabled={false}>
            <Stack direction="row" spacing={2}>
              <IconLink
                color="text.primary"
                variant="body2"
                endIcon={<ArrowForward color="inherit" fontSize="small" />}
                onClick={() => handleContinue(tool.output.chatSessionId!)}
              >
                {t(`continueCTA`)}
              </IconLink>
            </Stack>
          </ChatToolResultIndicator>
        );
      }
  }
};
